import { Fragment, useEffect, useState, type CSSProperties } from 'react'
import { useCustomFields, viewTypeDe } from '../admin/custom-field-data.ts'
import { SectionHeader } from './bits.tsx'
import {
  CampoInput,
  camposDoGrupo,
  filtrarVisiveis,
  rotuloCampo,
  useCustomValues,
  visivelPorRegra,
  type ItemParaRegras,
} from './campos-shared.tsx'
import { CampoInfo } from './CampoInfo.tsx'

/**
 * Campos complementares no detalhe do Item (SPEC-20260828-1914) — os TÓPICOS
 * que aparecem no corpo. Cada tópico tem um tipo de visualização (config.viewType):
 *  - simple: seção recolhível sozinha (padrão).
 *  - tab: entram numa barra de abas (lado a lado, uma expandindo por vez), igual
 *    a "Movimentação/Histórico".
 * O tipo "form" NÃO aparece aqui — vira botão na barra de propriedades
 * (ver CamposComplementaresProps). Grava no JSONB `customValues`.
 */

export function CamposComplementaresItem({
  workItemId,
  workItemTypeId,
  customValues,
  fieldHelp,
  item,
  expandirSinal = 0,
  expandirValor = true,
  ocultos,
}: {
  workItemId: string
  workItemTypeId: string
  customValues: Record<string, unknown>
  /** Instrucoes personalizadas do item ({ chave: html }) — para o "i". */
  fieldHelp?: Record<string, string>
  item: ItemParaRegras
  /** "Expandir/Recolher tudo" do detalhe: nonce + valor propagam aos tópicos. */
  expandirSinal?: number
  expandirValor?: boolean
  /** Tópicos desligados no menu "..." do item (chaves `topic:<id>`). */
  ocultos?: ReadonlySet<string>
}) {
  // sem filtro por tipo aqui: quem define onde/quando o campo aparece é a
  // colocação + as condições. Todos os campos colocados no tópico aparecem.
  const dados = useCustomFields('work_item')
  const { valores, setValores, gravar } = useCustomValues(workItemId, customValues)
  const [recolhidas, setRecolhidas] = useState<Set<string>>(new Set())
  const [tabsRecolhido, setTabsRecolhido] = useState(false)
  const [abaAtiva, setAbaAtiva] = useState<string | null>(null)

  const campos = (dados.data?.fields ?? []).filter((f) => f.defaultVisible)
  const grupos = dados.data?.groups ?? []
  const placements = dados.data?.placements ?? []

  const porGrupo = (gid: string) => filtrarVisiveis(camposDoGrupo(campos, placements, gid), item, valores)

  // o tópico pertence ao tipo do item? SEM nenhum tipo marcado, o tópico não
  // aparece em item algum (pedido 2026-08-29).
  const tipoPertence = (config: Record<string, unknown> | null | undefined) => {
    const tipos = config?.['workItemTypeIds']
    return Array.isArray(tipos) && tipos.includes(workItemTypeId)
  }
  // o tópico aparece se pertencer ao tipo, a regra bater E tiver campo visível
  const visiveis = grupos.filter(
    (g) =>
      g.kind === 'topic' &&
      // desligado no menu "..." do item (2026-08-30)
      !ocultos?.has(`topic:${g.id}`) &&
      tipoPertence(g.config) &&
      visivelPorRegra(g.config, item, valores) &&
      porGrupo(g.id).length > 0,
  )
  const simples = visiveis.filter((g) => viewTypeDe(g.config) === 'simple')
  const abasTab = visiveis.filter((g) => viewTypeDe(g.config) === 'tab')

  // "Expandir/Recolher tudo" recolhe/expande os simples e a barra de abas
  useEffect(() => {
    if (expandirSinal === 0) return
    setRecolhidas(expandirValor ? new Set() : new Set(simples.map((g) => g.id)))
    setTabsRecolhido(!expandirValor)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandirSinal])

  // aba ativa padrão: a primeira da barra, quando houver
  useEffect(() => {
    if (abasTab.length === 0) {
      if (abaAtiva !== null) setAbaAtiva(null)
    } else if (!abaAtiva || !abasTab.some((g) => g.id === abaAtiva)) {
      setAbaAtiva(abasTab[0]!.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abasTab.map((g) => g.id).join(',')])

  const alternar = (id: string) =>
    setRecolhidas((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  const iconeDe = (config: Record<string, unknown> | null | undefined) => {
    const classe = (config?.['icon'] as string) || ''
    return classe && !classe.startsWith('/') ? classe : 'fas fa-folder'
  }

  const bloco = (lista: ReturnType<typeof porGrupo>) => (
    <div className="wi-cf__grid">
      {lista.map(({ campo: f, width }) => (
        <div key={f.id} className="wi-cf__campo" style={{ '--cf-w': width } as CSSProperties}>
          <label className="wi-cf__label">
            {rotuloCampo(f.labels, f.key)}
            {f.required ? <span className="wi-cf__obrig"> *</span> : null}
            <CampoInfo
              chave={f.key}
              titulo={rotuloCampo(f.labels, f.key)}
              ajudaPadrao={typeof f.config.help === 'string' ? f.config.help : null}
              workItemId={workItemId}
              fieldHelp={fieldHelp}
            />
          </label>
          <CampoInput f={f} valores={valores} setValores={setValores} gravar={gravar} escopo={workItemId} />
        </div>
      ))}
    </div>
  )

  if (simples.length === 0 && abasTab.length === 0) return null

  const gAtiva = abasTab.find((g) => g.id === abaAtiva) ?? abasTab[0]

  return (
    <>
      {/* tópicos SIMPLES: cada um como seção recolhível */}
      {simples.map((g) => {
        const aberto = !recolhidas.has(g.id)
        const descricao = (g.config?.['description'] as string) || ''
        return (
          <div key={g.id} className="wi-sec--pad">
            <SectionHeader
              icon={iconeDe(g.config)}
              title={rotuloCampo(g.labels, 'Tópico')}
              info={
                <CampoInfo
                  chave={`topic:${g.id}`}
                  titulo={rotuloCampo(g.labels, 'Tópico')}
                  ajudaPadrao={typeof g.config?.['help'] === 'string' ? (g.config['help'] as string) : null}
                  workItemId={workItemId}
                  fieldHelp={fieldHelp}
                />
              }
              count={porGrupo(g.id).length}
              open={aberto}
              onToggle={() => alternar(g.id)}
            />
            {aberto ? (
              <div className="wi-sec__body">
                {descricao ? <p className="wi-cf__abadesc">{descricao}</p> : null}
                {bloco(porGrupo(g.id))}
              </div>
            ) : null}
          </div>
        )
      })}

      {/* tópicos ABA: barra de abas lado a lado (igual Movimentação/Histórico) */}
      {abasTab.length > 0 ? (
        <div className="wi-sec--pad">
          <div className="wi-tabs" role="tablist" aria-label="Tópicos complementares">
            <button
              type="button"
              className="wi-tabs__collapse"
              aria-label="Recolher/expandir"
              aria-expanded={!tabsRecolhido}
              title="Recolher/expandir"
              onClick={() => setTabsRecolhido((v) => !v)}
            >
              <i className={`fas fa-chevron-${tabsRecolhido ? 'right' : 'down'}`} aria-hidden />
            </button>
            {abasTab.map((g) => {
              const ativa = !tabsRecolhido && g.id === gAtiva?.id
              return (
                <Fragment key={g.id}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={ativa}
                    className={`wi-tab${ativa ? ' wi-tab--on' : ''}`}
                    onClick={() => {
                      setAbaAtiva(g.id)
                      setTabsRecolhido(false)
                    }}
                  >
                    <i className={iconeDe(g.config)} aria-hidden /> {rotuloCampo(g.labels, 'Tópico')}
                    <span className="wi-tab__count">{porGrupo(g.id).length}</span>
                  </button>
                  {/* "i" do topico em ABA: fora do <button> (button dentro de
                      button e invalido) — aparece colado na aba ativa */}
                  {ativa ? (
                    <CampoInfo
                      chave={`topic:${g.id}`}
                      titulo={rotuloCampo(g.labels, 'Tópico')}
                      ajudaPadrao={typeof g.config?.['help'] === 'string' ? (g.config['help'] as string) : null}
                      workItemId={workItemId}
                      fieldHelp={fieldHelp}
                    />
                  ) : null}
                </Fragment>
              )
            })}
          </div>
          {!tabsRecolhido && gAtiva ? (
            <div className="wi-sec__body" role="tabpanel">
              {(gAtiva.config?.['description'] as string) ? (
                <p className="wi-cf__abadesc">{gAtiva.config!['description'] as string}</p>
              ) : null}
              {bloco(porGrupo(gAtiva.id))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
